import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { AlertCircle, MessageSquare, Mail, Phone } from 'lucide-react';

const MessagingConsentDialog = ({ open, onConsent }) => {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConsent = async () => {
    if (!agreed) return;
    setLoading(true);
    try {
      await onConsent();
      // Success - loading will stop when page reloads or dialog closes
    } catch (error) {
      // Error occurred, stop loading so user can try again
      console.error('Consent handler error:', error);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center space-x-2 text-xl">
            <MessageSquare className="w-6 h-6 text-blue-600" />
            <span>SMS & Email Communication Consent</span>
          </DialogTitle>
          <DialogDescription>
            Please review and accept our messaging terms to continue using Hines Time
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          <div className="space-y-4 text-sm">
            {/* Alert Banner */}
            <div className="bg-blue-50 dark:bg-blue-950/40 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-300 flex-shrink-0 mt-0.5" />
              <p className="text-blue-900 dark:text-blue-100 font-medium">
                Hines Time requires SMS and email messaging to function properly. You will receive notifications about job assignments, team updates, and important account information.
              </p>
            </div>

            {/* Program Description */}
            <div className="space-y-2">
              <h3 className="font-bold text-gray-900 dark:text-white text-base">Hines Time Messaging Program</h3>
              <p className="text-gray-700 dark:text-slate-300">
                By using Hines Time, you agree to receive SMS text messages and emails from the platform. These messages include:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-700 dark:text-slate-300">
                <li>Job assignment notifications</li>
                <li>Team and schedule updates</li>
                <li>Time tracking reminders</li>
                <li>Safety and compliance alerts</li>
                <li>Account and security notifications</li>
                <li>System updates and important announcements</li>
              </ul>
              <p className="text-gray-700 dark:text-slate-300">
                <strong>Message frequency:</strong> Message frequency varies based on your job assignments and team activities. You may receive multiple messages per day when jobs are assigned or updated.
              </p>
            </div>

            {/* Opt-Out Information */}
            <div className="space-y-2">
              <h3 className="font-bold text-gray-900 dark:text-white text-base">How to Opt-Out</h3>
              <p className="text-gray-700 dark:text-slate-300">
                You can cancel SMS messages at any time. Simply text <strong>"STOP"</strong> to any message you receive from Hines Time. After you send "STOP", we will send you a confirmation message that you have been unsubscribed. You will no longer receive SMS messages from us.
              </p>
              <p className="text-gray-700 dark:text-slate-300">
                <strong>Important:</strong> Opting out of SMS messages may limit your ability to receive timely job notifications. You can re-enable SMS messaging in your account settings at any time.
              </p>
              <p className="text-gray-700 dark:text-slate-300">
                To opt-out of email notifications, you can adjust your preferences in Settings or click the unsubscribe link in any email we send you.
              </p>
            </div>

            {/* Help Information */}
            <div className="space-y-2">
              <h3 className="font-bold text-gray-900 dark:text-white text-base">Need Help?</h3>
              <p className="text-gray-700 dark:text-slate-300">
                If you are experiencing issues with messaging, you can:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-700 dark:text-slate-300">
                <li>Reply with <strong>"HELP"</strong> to any SMS message for assistance</li>
                <li>Contact support at <strong>support@hinestime.com</strong></li>
                <li>Visit our Support page in the app</li>
              </ul>
            </div>

            {/* Carrier Information */}
            <div className="space-y-2">
              <h3 className="font-bold text-gray-900 dark:text-white text-base">Carrier & Data Rates</h3>
              <p className="text-gray-700 dark:text-slate-300">
                <strong>Message and data rates may apply.</strong> Standard messaging rates from your mobile carrier will apply to all SMS messages sent and received. If you have questions about your text plan or data plan, please contact your wireless provider.
              </p>
              <p className="text-gray-700 dark:text-slate-300">
                Carriers are not liable for delayed or undelivered messages.
              </p>
            </div>

            {/* Privacy */}
            <div className="space-y-2">
              <h3 className="font-bold text-gray-900 dark:text-white text-base">Privacy & Data Protection</h3>
              <p className="text-gray-700 dark:text-slate-300">
                Your privacy is important to us. For information about how we handle your personal data, please read our Privacy Policy available in Settings.
              </p>
              <p className="text-gray-900 dark:text-yellow-100 font-semibold bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 rounded p-3">
                <strong>Data Sharing:</strong> We do not share, sell, or distribute your SMS opt-in data and consent to any third parties for marketing purposes. Your phone number and messaging consent information are used solely for Hines Time platform communications.
              </p>
            </div>

            {/* When Messages Start */}
            <div className="space-y-2">
              <h3 className="font-bold text-gray-900 dark:text-white text-base">When Will I Receive Messages?</h3>
              <p className="text-gray-700 dark:text-slate-300">
                Hines Time will only send messages once your user profile has been created and you have provided consent. Messages will be sent based on:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-700 dark:text-slate-300">
                <li>Job assignments to your profile</li>
                <li>Team schedule changes</li>
                <li>Time tracking events (wellness checks, auto-timeout)</li>
                <li>Safety and compliance requirements</li>
                <li>Account security events (password resets, 2FA)</li>
              </ul>
            </div>

            {/* Agreement Statement */}
            <div className="bg-gray-50 dark:bg-[#0F1923] border-2 border-gray-200 dark:border-[#2D3748] rounded-lg p-4 space-y-2">
              <h3 className="font-bold text-gray-900 dark:text-white text-base">Your Agreement</h3>
              <p className="text-gray-700 dark:text-slate-300">
                By checking the box below and continuing to use Hines Time, you:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-700 dark:text-slate-300">
                <li>Acknowledge you have read and understand these messaging terms</li>
                <li>Consent to receive SMS text messages and emails from Hines Time</li>
                <li>Understand message and data rates may apply</li>
                <li>Understand you can opt-out at any time by texting "STOP"</li>
                <li>Confirm the phone number and email on your account are correct</li>
                <li>Agree this consent is required to use Hines Time services</li>
              </ul>
            </div>

            {/* Contact Information */}
            <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Phone className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                <h3 className="font-bold text-gray-900 dark:text-white">Customer Care</h3>
              </div>
              <p className="text-sm text-gray-700 dark:text-slate-300">
                <strong>Email:</strong> support@hinestime.com<br />
                <strong>Support:</strong> Available via in-app support chat<br />
                <strong>Help Keyword:</strong> Reply "HELP" to any SMS for assistance
              </p>
            </div>
          </div>
        </ScrollArea>

        {/* Consent Checkbox and Button */}
        <div className="flex-shrink-0 border-t pt-4 space-y-4">
          <div className="flex items-start space-x-3 bg-yellow-50 dark:bg-yellow-950/40 border-2 border-yellow-300 dark:border-yellow-800 rounded-lg p-4">
            <Checkbox
              id="messaging-consent"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="mt-1"
            />
            <Label
              htmlFor="messaging-consent"
              className="text-sm font-semibold text-gray-900 dark:text-yellow-100 cursor-pointer leading-tight"
            >
              I agree to receive SMS text messages and emails from Hines Time as described above. I understand that message and data rates may apply, and I can opt-out at any time by texting "STOP".
            </Label>
          </div>
          
          <Button
            onClick={handleConsent}
            disabled={!agreed || loading}
            className="w-full h-12 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold text-base"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Processing...
              </>
            ) : (
              <>
                <MessageSquare className="w-5 h-5 mr-2" />
                Accept & Continue to Hines Time
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            This consent is required to use Hines Time. You cannot proceed without accepting these terms.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MessagingConsentDialog;
